import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Eye, FileText, ArrowRight, Calculator
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import LineItemsForm from '../../components/LineItemsForm';

import { generateDocumentHTML } from '../../lib/documentTemplates';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const Estimates = () => {
  const { currentCompany } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
    discount: 0,
    discount_type: 'fixed',
    valid_until: '',
    notes: '',
    status: 'draft',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [estimatesRes, clientsRes] = await Promise.all([
        api.get('/estimates'),
        api.get('/clients')
      ]);
      setEstimates(estimatesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClient = (clientId) => clients.find(c => c.id === clientId);

  const calculateTotals = (items, discount, discountType) => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const tax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const discountAmount = discountType === 'percent' 
      ? (subtotal * (parseFloat(discount) || 0) / 100)
      : (parseFloat(discount) || 0);
    const total = subtotal + tax - discountAmount;
    return { subtotal, tax, discount: discountAmount, total };
  };

  const openForm = (estimate = null) => {
    if (estimate) {
      setEditingEstimate(estimate);
      setFormData({
        client_id: estimate.client_id || '',
        items: estimate.items?.length > 0 ? estimate.items : [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        discount: estimate.discount || 0,
        discount_type: estimate.discount_type || 'fixed',
        valid_until: estimate.valid_until || '',
        notes: estimate.notes || '',
        status: estimate.status || 'draft',
      });
    } else {
      setEditingEstimate(null);
      setFormData({
        client_id: '',
        items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        discount: 0,
        discount_type: 'fixed',
        valid_until: '',
        notes: '',
        status: 'draft',
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    setSubmitting(true);
    try {
      const totals = calculateTotals(formData.items, formData.discount, formData.discount_type);
      const payload = { ...formData, ...totals, discount_amount: totals.discount };

      if (editingEstimate) {
        await api.put(`/estimates/${editingEstimate.id}`, payload);
        toast.success('Estimate updated');
      } else {
        await api.post('/estimates', payload);
        toast.success('Estimate created');
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save estimate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this estimate?')) {
      try {
        await api.delete(`/estimates/${id}`);
        toast.success('Estimate deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  const openPreview = (estimate) => {
    setSelectedEstimate(estimate);
    const client = getClient(estimate.client_id);
    const docWithClient = {
      ...estimate,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
    };
    const html = generateDocumentHTML('classic', 'estimate', docWithClient, currentCompany);
    setPreviewHtml(html.replace('QUOTATION', 'ESTIMATE'));
    setIsPreviewOpen(true);
  };

  const handlePrint = (estimate) => {
    const client = getClient(estimate.client_id);
    const docWithClient = { ...estimate, client_name: client?.name || 'Client' };
    const html = generateDocumentHTML('classic', 'estimate', docWithClient, currentCompany);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html.replace('QUOTATION', 'ESTIMATE'));
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const convertToQuotation = async (estimate) => {
    try {
      await api.post('/quotations', {
        ...estimate,
        source_estimate_id: estimate.id,
        status: 'draft',
      });
      toast.success('Quotation created from estimate');
    } catch (error) {
      toast.error('Failed to convert');
    }
  };

  const filteredEstimates = estimates.filter(e => {
    const client = getClient(e.client_id);
    return e.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client?.name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Estimates</h1>
          <p className="text-slate-500 mt-1">Create preliminary estimates before formal quotations</p>
        </div>
        <Button onClick={() => openForm()} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="h-4 w-4 mr-2" />Create Estimate
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search estimates..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Estimate #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEstimates.length > 0 ? (
                filteredEstimates.map((estimate) => (
                  <TableRow key={estimate.id}>
                    <TableCell className="font-medium">#{estimate.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>{getClient(estimate.client_id)?.name || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{estimate.items?.length || 0} items</Badge></TableCell>
                    <TableCell className="text-right font-medium">${(estimate.total || 0).toLocaleString()}</TableCell>
                    <TableCell>{estimate.valid_until ? new Date(estimate.valid_until).toLocaleDateString() : '-'}</TableCell>
                    <TableCell><Badge className={STATUS_COLORS[estimate.status]}>{estimate.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openPreview(estimate)}><Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePrint(estimate)}><Printer className="h-4 w-4 mr-2" />Print</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => convertToQuotation(estimate)}><ArrowRight className="h-4 w-4 mr-2" />Convert to Quotation</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openForm(estimate)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(estimate.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-slate-500"><Calculator className="h-8 w-8 mx-auto mb-2 text-slate-300" />No estimates found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEstimate ? 'Edit Estimate' : 'Create Estimate'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <LineItemsForm
              items={formData.items}
              onItemsChange={(items) => setFormData({ ...formData, items })}
              clients={clients}
              onClientsChange={setClients}
              selectedClientId={formData.client_id}
              onClientChange={(id) => setFormData({ ...formData, client_id: id })}
              discount={formData.discount}
              onDiscountChange={(val) => setFormData({ ...formData, discount: val })}
              discountType={formData.discount_type}
              onDiscountTypeChange={(val) => setFormData({ ...formData, discount_type: val })}
            />
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="grid gap-2">
                <Label>Valid Until</Label>
                <Input type="date" value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2 mt-4">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingEstimate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Estimate Preview</DialogTitle></DialogHeader>
          
          <div className="border rounded-lg overflow-hidden mt-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => selectedEstimate && handlePrint(selectedEstimate)}><Printer className="h-4 w-4 mr-2" />Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Estimates;
